import React from "react";
import { GetStaticProps, GetStaticPropsResult, NextPage } from "next";
import styled from "styled-components";
import {
  MissingComponentHandler,
  PortableTextComponents,
} from "@portabletext/react";
import {
  OakHeading,
  OakP,
  OakFlex,
  OakMaxWidth,
  OakBox,
} from "@oaknational/oak-components";

import CMSClient from "@/node-lib/cms";
import { ContactPage } from "@/common-lib/cms-types";
import Layout from "@/components/AppComponents/AppLayout";
import Card from "@/components/SharedComponents/Card";
import { useNewsletterForm } from "@/components/GenericPagesComponents/NewsletterForm";
import SummaryCard from "@/components/SharedComponents/Card/SummaryCard";
import { getSeoProps } from "@/browser-lib/seo/getSeoProps";
import NewsletterFormWrap from "@/components/GenericPagesComponents/NewsletterFormWrap";
import getPageProps from "@/node-lib/getPageProps";
import { PortableTextWithDefaults } from "@/components/SharedComponents/PortableText";
import { TopNavProps } from "@/components/AppComponents/TopNav/TopNav";
import curriculumApi2023 from "@/node-lib/curriculum-api-2023";
import { TeacherBrowseAnalyticsStoreProvider } from "@/context/TeacherBrowseAnalytics/TeacherBrowseAnalyticsProvider";

export type ContactPageProps = {
  pageData: ContactPage;
  topNav: TopNavProps;
};

// @TODO: extract
const logMissingPortableTextComponents: MissingComponentHandler = (
  message,
  options,
) => {
  console.log(message, {
    type: options.type,
    nodeType: options.nodeType,
  });
};

const BodyHeading = styled(OakHeading)`
  &:first-child {
    margin-top: 0;
  }

  & + p {
    margin-top: 0;
  }
`;

const portableTextComponents: PortableTextComponents = {
  block: {
    sectionHeading: (props) => {
      return (
        <BodyHeading
          $font={"heading-5"}
          tag={"h2"}
          $mt="spacing-32"
          $mb="spacing-8"
        >
          {props.children}
        </BodyHeading>
      );
    },
    normal: (props) => {
      return <OakP $mt={["spacing-16", "spacing-24"]}>{props.children}</OakP>;
    },
  },
};

const ContactUs: NextPage<ContactPageProps> = ({ pageData, topNav }) => {
  const newsletterFormProps = useNewsletterForm();

  return (
    <TeacherBrowseAnalyticsStoreProvider
      programmeState={null}
      accessLevel="homepage"
    >
      <Layout
        seoProps={getSeoProps(pageData.seo)}
        $background={"bg-primary"}
        topNavProps={topNav}
      >
        <OakMaxWidth
          $pt={["spacing-72", "spacing-80"]}
          $pb={["spacing-64", "spacing-80"]}
        >
          <SummaryCard {...pageData} />
          <Card
            $borderRadius={"border-radius-l"}
            $justifyContent={"space-between"}
            $background={"bg-decorative4-subdued"}
            $ph={["spacing-16", "spacing-24"]}
            $pv={["spacing-24"]}
            $mt={["spacing-72", "spacing-80"]}
            $font={["body-2", "body-1"]}
          >
            <OakFlex
              $alignItems={["flex-start", "center"]}
              $flexDirection={["column", "row"]}
            >
              <OakBox $maxWidth={"spacing-640"}>
                <PortableTextWithDefaults
                  components={portableTextComponents}
                  value={pageData.bodyPortableText}
                  onMissingComponent={logMissingPortableTextComponents}
                />
              </OakBox>
              <NewsletterFormWrap
                {...newsletterFormProps}
                containerProps={{
                  $display: ["none", "flex"],
                  $minWidth: "spacing-360",
                  $ml: "spacing-56",
                }}
              />
            </OakFlex>
          </Card>
          <NewsletterFormWrap
            {...newsletterFormProps}
            containerProps={{ $display: ["flex", "none"], $mt: "spacing-32" }}
          />
        </OakMaxWidth>
      </Layout>
    </TeacherBrowseAnalyticsStoreProvider>
  );
};

export const getStaticProps: GetStaticProps<ContactPageProps> = async (
  context,
) => {
  return getPageProps({
    page: "contact-us::getStaticProps",
    context,
    getProps: async () => {
      const isPreviewMode = context.preview === true;

      const pageData = await CMSClient.contactPage({
        previewMode: isPreviewMode,
      });

      if (!pageData) {
        return {
          notFound: true,
        };
      }
      const topNav = await curriculumApi2023.topNav();

      const results: GetStaticPropsResult<ContactPageProps> = {
        props: {
          pageData,
          topNav,
        },
      };

      return results;
    },
  });
};

export default ContactUs;
